// Self-hosted Inter (variable, weights 100–900) — the site's default sans. The
// @fontsource import bundles the woff2 through Vite, so it loads offline with no
// network font request (the family name is "Inter Variable"; see tailwind.config
// fontFamily.sans + --font-sans in styles.css).
import "@fontsource-variable/inter";
import "katex/dist/katex.min.css";
import "./styles.css";
// Side-effect: apply the stored brand theme (--logo-* vars + favicon) before
// the first paint of the logo.
import "./stores/theme";

import React from "react";
import ReactDOM from "react-dom/client";

import App from "./App";
import { setupPWA } from "./lib/pwa";
import { publicDocSlug } from "./public-docs/route";

// Excalidraw (the drawing surface) loads its fonts relative to this base. The
// files are copied from the package into public/excalidraw-assets/ by
// scripts/copy-excalidraw-assets.mjs (runs pre-dev/build), so they self-host —
// no CDN, and the PWA can precache them for offline.
(window as unknown as { EXCALIDRAW_ASSET_PATH?: string }).EXCALIDRAW_ASSET_PATH =
  "/excalidraw-assets/";

// Desktop boot handshake: the launcher opens /?token=<per-install token>.
// Convert it into a SameSite cookie once — every request path (fetch, SSE,
// pdf.js byte-ranges, <img>) then authenticates automatically — and scrub
// the token from the address bar / history.
const bootToken = new URLSearchParams(window.location.search).get("token");
if (bootToken) {
  document.cookie = `graphmind_token=${encodeURIComponent(bootToken)}; path=/; SameSite=Strict; max-age=31536000`;
  const url = new URL(window.location.href);
  url.searchParams.delete("token");
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
}

const root = ReactDOM.createRoot(document.getElementById("root")!);

// A published document (`/read/<slug>`) has its own entry and its own
// prerendered page — this shell should never be what answers that URL. But it
// CAN be: a service worker still running a build from before those pages
// existed falls navigations back to index.html, and so does any static host
// with a blanket SPA rewrite. Rather than show a stranger an app they cannot
// sign into, render the document here too. Lazily, so the studio's own boot
// path is untouched and the reading page's chunk only loads on that URL.
const publicSlug = publicDocSlug(window.location.pathname);
if (publicSlug) {
  void import("./public-docs/mount").then(({ publicDocElement }) =>
    root.render(<React.StrictMode>{publicDocElement(publicSlug)}</React.StrictMode>),
  );
} else {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

// Register the service worker (installable + offline shell + update prompt).
// Runs on the fallback path too: it is what replaces the stale worker that
// mis-routed the navigation in the first place.
setupPWA();
